<template>
  <div>
    <v-table class="table" density="compact" v-if="month">
      <thead>
        <tr>
          <th class="text-left name">
            <h2>{{monthTitle}}</h2>
          </th>
          <th
            v-for="day in month.days"
            :key="day.day"
            class="small text-center"
            :class="{
              'bg-red-lighten-4': day.isWeekend,
              'bg-red-lighten-2': day.isHoliday
            }"
          >{{day.day.toString().padStart(2, '0')}}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="technic in technics" :key="technic.id">
          <th class="name">{{technic.fullName}}</th>
          <td
            class="small"
            v-for="day in month.days"
            :key="day.day"
            :style="{backgroundColor: bg(technic, day)}"
            @click="select(technic, day, selectedWorkday)"
            @mousedown.stop="selectionStart(technic, day, selectedWorkday)"
            @mouseup.stop="selectionEnd()"
            @mouseover="selectionHover(technic, day, selectedWorkday)"
          ></td>
        </tr>
      </tbody>
    </v-table>
    <v-progress-linear
      :active="loading"
      :indeterminate="loading"
      color="deep-purple-accent-4"
    />
  </div>
</template>

<script>
import api from './../api'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'
import { capitalize } from './../plugins/utils'

export default {
  props: {
    technics: {
      required: true
    },
    startDate: {
      required: true
    },
    endDate: {
      required: true
    },
    selectedWorkday: {
      required: false
    },
    workdays: {
      required: true
    },
    // A technician's planner: nothing is picked, painted or sent.
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      date: null,
      month: null,
      data: [],
      selectedTechnic: null,
      map: {},
      updates: []
    }
  },
  computed: {
    monthTitle () {
      return capitalize(dayjs(this.startDate).format('MMMM'))
    }
  },
  watch: {
    startDate () {
      this.refresh()
    }
  },
  methods: {
    async refresh () {
      try {
        this.loading = true
        const [ month, data ] = await Promise.all([
          api.getPlannerMonth({ date: this.startDate.toISOString() }),
          api.getPlanner({ startDate: this.startDate.toISOString(), endDate: this.endDate.toISOString() })
        ])
        this.month = month
        this.data = data.events
        this.buildMap()
      } catch (err) {
        // ignore
      } finally {
        this.loading = false
      }
    },
    select (technic, day, selectedWorkday) {
      if (!selectedWorkday || this.readonly) {
        return
      }
      if (selectedWorkday.title === 'tyhi') {
        this.map[technic.id][day.day] = null
      } else {
        this.map[technic.id][day.day] = selectedWorkday
      }
      this.updates[technic.id + '_' + day.date] = { technic, day, workday: selectedWorkday }
      this.loading = true
      this.sendUpdates()
    },
    selectionStart (technic, day, selectedWorkday) {
      this.selectedTechnic = technic
      this.select(technic, day, selectedWorkday)
    },
    selectionEnd () {
      this.selectedTechnic = null
    },
    selectionHover (technic, day, selectedWorkday) {
      if (this.selectedTechnic !== technic) {
        return
      }
      this.select(technic, day, selectedWorkday)
    },
    bg (technic, day) {
      const workday = this.map[technic.id] && this.map[technic.id][day.day]
      if (workday) {
        return '#' + workday.color
      }
    },
    buildMap () {
      const map = {}
      this.technics.forEach(technic => {
        map[technic.id] = {}
        this.month.days.forEach(day => {
          map[technic.id][day.day] = null
        })
      })
      this.data.forEach(event => {
        const day = dayjs(event.startDate).get('date')
        if (!map[event.user.id]) {
          return
        }
        map[event.user.id][day] = this.workdays.find(({ id }) => event.workday.id === id)
      })
      this.map = map
    }
  },
  mounted () {
    this.refresh()
  },
  created () {
    this.sendUpdates = debounce(() => {
      this.loading = true
      api.addToPlanner(Object.values(this.updates).map(update => {
        return {
          technicId: update.technic.id,
          date: update.day.date,
          workdayId: update.workday.title !== 'tyhi' && update.workday.id
        }
      })).catch(err => {
        this.$store.dispatch('showNotification', { text: err.message, color: 'red' })
      }).finally(() => {
        this.loading = false
      })
      this.updates = {}
    }, 2000)
  }
}
</script>

<style lang="scss">
.small {
  font-size: 10px;
  padding: 0 !important;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}
.name {
  white-space: nowrap;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}
.table {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>